import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert, Button, Card, Input, Modal, Space, Table, Tag, Tabs, Avatar, Spin, message,
  Row, Col, Select, Typography
} from 'antd';
import {
  MailOutlined, UserDeleteOutlined, EditOutlined, UserAddOutlined, CheckCircleOutlined, FilterOutlined
} from '@ant-design/icons';
import { fetchChannels, updateChannel, deleteChannel, batchUnfollowChannels, batchSendEmails, followChannel, fetchFilterOptions } from '../../utils/api';
import { getCountryName, countries } from '../../utils/countryNames.js';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

/* -------------------- 筛选器组件 -------------------- */
function FilterPanel({ filters, setFilters, regions, platforms, games, onApply }) {
  return (
    <Card style={{ marginBottom: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="bottom">
          <Col flex="auto">
            <Space size="large" wrap={false}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>分类</Text>
                <Select
                  value={filters.category}
                  onChange={value => setFilters(f => ({ ...f, category: value }))}
                  style={{ width: 150 }}
                  placeholder="选择分类"
                >
                  <Option value="game">游戏</Option>
                  <Option value="short">短剧</Option>
                </Select>
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>地区</Text>
                <Select
                  value={filters.region}
                  onChange={value => setFilters(f => ({ ...f, region: value }))}
                  style={{ width: 200 }}
                  placeholder="选择地区"
                >
                  <Option value="all">全部地区</Option>
                  {regions.map((region) => (
                    <Option key={region.id} value={region.id}>{region.name}</Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>平台</Text>
                <Select
                  value={filters.platform}
                  onChange={value => setFilters(f => ({ ...f, platform: value }))}
                  style={{ width: 200 }}
                  placeholder="选择平台"
                >
                  <Option value="all">全部平台</Option>
                  {platforms.map((platform) => (
                    <Option key={platform.id} value={platform.id}>{platform.name}</Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>游戏</Text>
                <Select
                  value={filters.game}
                  onChange={value => setFilters(f => ({ ...f, game: value }))}
                  style={{ width: 200 }}
                  placeholder="选择游戏"
                >
                  <Option value="0">全部游戏</Option>
                  {games.map((game) => (
                    <Option key={game.gameId} value={game.gameId}>{game.cName}</Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>频道名称</Text>
                <Input
                  value={filters.name}
                  placeholder="请输入频道名称"
                  allowClear
                  style={{ width: 200, height: 32, alignItems: "center" }}
                  onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
                  onPressEnter={onApply}
                />
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={onApply}
              >
                应用筛选
              </Button>
            </Space>
          </Col>
        </Row>
      </Space>
    </Card>
  );
}

/* -------------------- 频道基本信息表格组件 -------------------- */
function BasicChannelsTable({
  filters,
  games,
  regions,
  platforms,
  onEdit,
  refreshTrigger,
}) {
  const [state, setState] = useState({ data: [], total: 0, loading: false, page: 1, pageSize: 10, selected: new Set() });

  // 计算筛选参数
  const getCountryCodes = useCallback(() => {
    if (filters.region === 'all') return undefined;
    const r = regions.find(rr => String(rr.id) === String(filters.region));
    return r?.countryCodes?.length ? r.countryCodes : undefined;
  }, [filters.region, regions]);

  const getPlatformCodes = useCallback(() => {
    if (filters.platform === 'all') return undefined;
    const p = platforms.find(pp => String(pp.id) === String(filters.platform));
    return p?.code ? [p.code] : undefined;
  }, [filters.platform, platforms]);

  // 加载数据
  const loadChannels = useCallback(async (page, pageSize) => {
    setState(p => ({ ...p, loading: true }));
    const countryCodes = getCountryCodes();
    const platformCodes = getPlatformCodes();
    const gameId = filters.game === '0' ? undefined : filters.game;

    const params = {
      page,
      pageSize,
      countryCodes,
      platformCodes,
      game: gameId,
      name: filters.name,
      category: filters.category
    };

    try {
      const { channels: data, total } = await fetchChannels('channels', params);
      setState(p => ({
        ...p,
        data: data || [],
        total: total || 0,
        loading: false,
      }));
    } catch (e) {
      setState(p => ({ ...p, loading: false }));
    }
  }, [filters, getCountryCodes, getPlatformCodes]);

  // 首次加载
  useEffect(() => {
    loadChannels(state.page, state.pageSize);
  }, []);

  // 筛选变化时重新加载
  useEffect(() => {
    setState(p => ({ ...p, page: 1 }));
    loadChannels(1, state.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.platform, filters.game, filters.name,filters.category]);

  // 刷新触发（编辑保存后）
  useEffect(() => {
    if (refreshTrigger) {
      loadChannels(state.page, state.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // 分页处理
  const handlePageChange = (page, size) => {
    setState(p => ({ ...p, page, pageSize: size }));
    loadChannels(page, size);
  };

  // 关注处理
  const handleFollow = (record) => {
    const id = record.channelId || record.id;
    setState(p => ({
      ...p,
      data: p.data.map((c) =>
        c.channelId === id ? { ...c, followed: true } : c
      ),
    }));

    followChannel(id)
      .then(() => message.success('关注成功'))
      .catch((e) => {
        message.error('关注失败：' + e.message);
        setState(p => ({
          ...p,
          data: p.data.map((c) =>
            c.channelId === id ? { ...c, followed: false } : c
          ),
        }));
      });
  };

  // 表格列定义
  const columns = [
    {
      title: '主播',
      dataIndex: 'id',
      width: 220,
      render: (_, r) => (
        <Space>
          <Avatar src={r.thumbnails?.default?.url} size={32}
            onClick={() => window.open(`https://www.youtube.com/channel/${r.channelId}`, '_blank')}
            style={{ cursor: 'pointer' }}
          />
          <span>{r.name}</span>
        </Space>
      ),
    },
    {
      title: '国家',
      dataIndex: 'country',
      width: 100,
      render: (c) => {
        if (!c) return '-';
        return getCountryName(c) || '-';
      }
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: 200,
      render: (e, record) => {
        if (!e) return '-';
        const source = record.emailSource;
        const sourceText = source === 'crawler' ? '系统获取' : source === 'manual' ? '手动' : '';
        return (
          <Space>
            <span>{e}</span>
            {sourceText && (
              <Tag color={source === 'crawler' ? 'blue' : 'green'} size="small">
                {sourceText}
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: '游戏',
      dataIndex: 'keywords',
      width: 200,
      render: (keywords = []) => (
        <Space wrap>
          {keywords.map((keyword, idx) => {
            const game = games.find((g) => g.gameId === keyword);
            return <Tag key={idx}>{game?.cName || '_'}</Tag>;
          })}
        </Space>
      ),
    },
    { title: '平台', dataIndex: 'platform', width: 100, render: (p) => <Tag color="geekblue">{p}</Tag> },
    {
      title: '操作',
      width: 220,
      render: (_, r) => {
        return (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)}>
              编辑
            </Button>
            {r.followed ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                已关注
              </Tag>
            ) : (
              <Button
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => handleFollow(r)}
              >
                关注
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card>
      <Spin spinning={state.loading}>
        <Table
          rowKey={(r) => r.channelId || r.id}
          dataSource={state.data}
          columns={columns}
          pagination={{
            current: state.page,
            pageSize: state.pageSize,
            total: state.total,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: handlePageChange,
          }}
          scroll={{ x: 1000 }}
        />
      </Spin>
    </Card>
  );
}

/* -------------------- 已关注频道表格组件 -------------------- */
function FollowedChannelsTable({
  filters,
  games,
  regions,
  platforms,
  onMail,
  refreshTrigger,
}) {
  const [state, setState] = useState({ data: [], total: 0, loading: false, page: 1, pageSize: 10, selected: new Set() });

  // 计算筛选参数
  const getCountryCodes = useCallback(() => {
    if (filters.region === 'all') return undefined;
    const r = regions.find(rr => String(rr.id) === String(filters.region));
    return r?.countryCodes?.length ? r.countryCodes : undefined;
  }, [filters.region, regions]);

  const getPlatformCodes = useCallback(() => {
    if (filters.platform === 'all') return undefined;
    const p = platforms.find(pp => String(pp.id) === String(filters.platform));
    return p?.code ? [p.code] : undefined;
  }, [filters.platform, platforms]);

  // 加载数据
  const loadChannels = useCallback(async (page, pageSize) => {
    setState(p => ({ ...p, loading: true }));
    const countryCodes = getCountryCodes();
    const platformCodes = getPlatformCodes();
    const gameId = filters.game === '0' ? undefined : filters.game;

    const params = {
      page,
      pageSize,
      countryCodes,
      platformCodes,
      game: gameId,
      name: filters.name,
      category: filters.category
    };

    try {
      const { channels: data, total } = await fetchChannels('followed-channels', params);
      setState(p => ({
        ...p,
        data: data || [],
        total: total || 0,
        loading: false,
      }));
    } catch (e) {
      setState(p => ({ ...p, loading: false }));
    }
  }, [filters, getCountryCodes, getPlatformCodes]);

  // 首次加载
  useEffect(() => {
    loadChannels(state.page, state.pageSize);
  }, []);

  // 筛选变化时重新加载
  useEffect(() => {
    setState(p => ({ ...p, page: 1 }));
    loadChannels(1, state.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.platform, filters.game, filters.name]);

  // 刷新触发（编辑保存后）
  useEffect(() => {
    if (refreshTrigger) {
      loadChannels(state.page, state.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // 分页处理
  const handlePageChange = (page, size) => {
    setState(p => ({ ...p, page, pageSize: size }));
    loadChannels(page, size);
  };

  // 选中处理
  const handleSelect = (keys) => {
    setState(p => ({ ...p, selected: new Set(keys) }));
  };

  const handleSelectAll = (selected) => {
    const ids = state.data.map((r) => r.channelId || r.id);
    setState(p => ({ ...p, selected: selected ? new Set(ids) : new Set() }));
  };

  // 取消关注
  const handleUnfollow = (channelId) => {
    deleteChannel(channelId).then(() => {
      message.success('已取消关注');
      setState(p => ({
        ...p,
        data: p.data.filter((c) => (c.channelId || c.id) !== channelId),
        total: p.total - 1,
        selected: (() => {
          const s = new Set(p.selected);
          s.delete(channelId);
          return s;
        })(),
      }));
    });
  };

  // 批量取消关注
  const handleBatchUnfollow = () => {
    const list = Array.from(state.selected);
    batchUnfollowChannels(list).then(() => {
      message.success(`已批量取消关注 ${list.length} 个频道`);
      setState(p => ({
        ...p,
        data: p.data.filter((c) => !list.includes(c.channelId || c.id)),
        total: p.total - list.length,
        selected: new Set(),
      }));
    });
  };

  // 批量发邮件
  const handleBatchMail = () => {
    const emails = state.data
      .filter((c) => state.selected.has(c.channelId || c.id))
      .map((c) => c.email)
      .filter(Boolean);
    if (!emails.length) return message.warning('选中频道无有效邮箱');
    onMail('batch', emails, state.selected.size);
  };

  // 表格列定义
  const columns = [
    {
      title: '主播',
      dataIndex: 'name',
      width: 220,
      render: (_, r) => (
        <Space>
          <Avatar src={r.thumbnails?.default?.url} size={32} onClick={() => window.open(`https://www.youtube.com/channel/${r.channelId}`, '_blank')} style={{ cursor: 'pointer' }} />
          <span>{r.name}</span>
        </Space>
      ),
    },
    {
      title: '国家',
      dataIndex: 'country',
      width: 100,
      render: (c) => {
        if (!c) return '-';
        return getCountryName(c) || '-';
      }
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: 200,
      render: (e, record) => {
        if (!e) return '-';
        const source = record.emailSource;
        const sourceText = source === 'crawler' ? '系统获取' : source === 'manual' ? '手动' : '';
        return (
          <Space>
            <span>{e}</span>
            {sourceText && (
              <Tag color={source === 'crawler' ? 'blue' : 'green'} size="small">
                {sourceText}
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: '游戏',
      dataIndex: 'keywords',
      width: 200,
      render: (keywords = []) => (
        <Space wrap>
          {keywords.map((keyword, idx) => {
            const game = games.find((g) => g.gameId === keyword);
            return <Tag key={idx}>{game?.cName || '_'}</Tag>;
          })}
        </Space>
      ),
    },
    { title: '平台', dataIndex: 'platform', width: 100, render: (p) => <Tag color="geekblue">{p}</Tag> },
    {
      title: '操作',
      width: 220,
      render: (_, r) => {
        const id = r.channelId || r.id;
        return (
          <Space>
            <Button size="small" icon={<MailOutlined />} onClick={() => onMail('single', [r.email])}>
              发邮件
            </Button>
            <Button size="small" danger icon={<UserDeleteOutlined />} onClick={() => handleUnfollow(id)}>
              取消关注
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      {state.selected.size > 0 && (
        <Alert
          message={`已选择 ${state.selected.size} 个频道`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Space>
              <Button type="primary" icon={<MailOutlined />} onClick={handleBatchMail}>
                批量发邮件
              </Button>
              <Button danger icon={<UserDeleteOutlined />} onClick={handleBatchUnfollow}>
                批量取消关注
              </Button>
            </Space>
          }
        />
      )}
      <Card>
        <Spin spinning={state.loading}>
          <Table
            rowKey={(r) => r.channelId || r.id}
            dataSource={state.data}
            columns={columns}
            pagination={{
              current: state.page,
              pageSize: state.pageSize,
              total: state.total,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: handlePageChange,
            }}
            rowSelection={{
              selectedRowKeys: Array.from(state.selected),
              onChange: handleSelect,
              onSelectAll: handleSelectAll,
            }}
            scroll={{ x: 1000 }}
          />
        </Spin>
      </Card>
    </>
  );
}

/* -------------------- 主组件 -------------------- */
export default function ChannelManagement() {
  const [activeTab, setActiveTab] = useState('basic');
  const [filters, setFilters] = useState({ region: 'all', platform: 'all', game: '0', name: '', category: 'game' });
  const [regions, setRegions] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [games, setGames] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  // 邮件模态框
  const [mail, setMail] = useState({ show: false, subject: '', content: '', mode: 'batch', emails: [], selectedCount: 0 });

  // 编辑模态框
  const [edit, setEdit] = useState({ show: false, record: null, form: { name: '', email: '', country: '' } });

  // 刷新触发器（用于触发表格刷新）
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // 初始化筛选项
  const initializeFilterData = useCallback(async () => {
    try {
      const filterRes = await fetchFilterOptions();
      const regionsRes = filterRes?.regions || [];
      const gamesRes = filterRes?.games || [];
      setRegions(regionsRes);

      let availablePlatforms = [];
      if (filters.region === 'all') {
        const seen = new Set();
        regionsRes.forEach(r => {
          (r.platforms || []).forEach(p => {
            const pid = String(p.id);
            if (!seen.has(pid)) {
              seen.add(pid);
              availablePlatforms.push(p);
            }
          });
        });
      } else {
        const currentRegion = regionsRes.find(r => String(r.id) === String(filters.region));
        availablePlatforms = currentRegion?.platforms || [];
      }
      setPlatforms(availablePlatforms);
      console.log('gamesRes', gamesRes);
      setGames(gamesRes);
    } catch (e) {
      console.log(e);
    }
  }, [filters.region]);

  useEffect(() => {
    initializeFilterData();
  }, [initializeFilterData]);

  // 加载国家列表（使用 i18n-iso-countries）
  useEffect(() => {
    try {
      // 获取中文国家名称（优先使用官方名称）
      const countryNamesZh = countries.getNames('zh', { select: 'official' });
      // 获取英文国家名称作为备用
      const countryNamesEn = countries.getNames('en', { select: 'official' });

      // 转换为 Ant Design Select 的 options 格式
      // countryNamesZh 是一个对象，键是国家代码，值是中文名称
      const options = Object.keys(countryNamesZh).map(code => {
        const nameZh = countryNamesZh[code];
        const nameEn = countryNamesEn[code];
        // 优先使用中文名称，如果没有则使用英文名称，最后使用代码本身
        const displayName = nameZh || nameEn || code;
        return {
          value: code, // ISO 3166-1 alpha-2 代码，如 'CN', 'US'
          label: displayName // 中文国家名称，如 '中国', '美国'
        };
      });

      // 按中文名称排序
      options.sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'));

      setCountryOptions(options);
    } catch (e) {
      console.error('加载国家列表失败:', e);
      setCountryOptions([]);
    }
  }, []);

  // 应用筛选
  const applyFilters = () => {
    // 筛选逻辑由各个表格组件自己处理
  };

  // 打开编辑模态框
  const openEdit = (record) => {
    setEdit({
      show: true,
      record,
      form: { name: record.name || '', email: record.email || '', country: record.country || '' },
    });
  };

  // 保存编辑
  const saveEdit = async () => {
    const id = edit.record.channelId || edit.record.id;
    try {
      await updateChannel(id, edit.form);
      message.success('已保存');
      setEdit({ show: false, record: null, form: { name: '', email: '', country: '' } });
      // 触发表格刷新
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      message.error('保存失败：' + (e?.message || '未知错误'));
    }
  };

  // 打开邮件模态框
  const openMail = (mode, emails, selectedCount = 0) => {
    if (mode === 'single' && !emails[0]) {
      return message.warning('该频道无有效邮箱');
    }
    setMail({ show: true, subject: '', content: '', mode, emails, selectedCount });
  };

  // 发送邮件
  const handleSendMail = async () => {
    const { subject, content } = mail;
    if (!subject.trim() || !content.trim()) return message.warning('请填写完整');

    try {
      const result = await batchSendEmails(mail.emails, { subject, content });
      if (result.success) {
        message.success(result.message || `成功发送 ${result.sentCount || 0} 封邮件`);
        setMail({ show: false, subject: '', content: '', mode: 'batch', emails: [], selectedCount: 0 });
      } else {
        message.warning(result.message || '部分邮件发送失败');
      }
    } catch (error) {
      message.error(error.message || '邮件发送失败');
    }
  };

  return (
    <div style={{ padding: 12 }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="频道基本信息" key="basic" />
        <TabPane tab="已关注频道" key="followed" />
      </Tabs>

      <FilterPanel
        filters={filters}
        setFilters={setFilters}
        regions={regions}
        platforms={platforms}
        games={games}
        onApply={applyFilters}
      />

      {activeTab === 'basic' ? (
        <BasicChannelsTable
          filters={filters}
          games={games}
          regions={regions}
          platforms={platforms}
          onEdit={openEdit}
          refreshTrigger={refreshTrigger}
        />
      ) : (
        <FollowedChannelsTable
          filters={filters}
          games={games}
          regions={regions}
          platforms={platforms}
          onEdit={openEdit}
          onMail={openMail}
          refreshTrigger={refreshTrigger}
        />
      )}

      {/* 邮件模态框 */}
      <Modal
        title={`发送邮件 (${mail.mode === 'batch' ? (mail.selectedCount || 0) : mail.emails.length} 个频道)`}
        open={mail.show}
        onCancel={() => setMail({ show: false, subject: '', content: '', mode: 'batch', emails: [], selectedCount: 0 })}
        onOk={handleSendMail}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {mail.mode === 'batch' && mail.selectedCount > mail.emails.length && (
            <Alert
              message={`已选 ${mail.selectedCount} 个频道，其中 ${mail.emails.length} 个有邮箱，其他将跳过发送。`}
              type="info"
              showIcon
            />
          )}
          <Input
            placeholder="邮件主题"
            value={mail.subject}
            onChange={(e) => setMail({ ...mail, subject: e.target.value })}
          />
          <TextArea
            placeholder="邮件内容"
            rows={6}
            value={mail.content}
            onChange={(e) => setMail({ ...mail, content: e.target.value })}
          />
        </Space>
      </Modal>

      {/* 编辑模态框 */}
      <Modal
        title="编辑频道"
        open={edit.show}
        onCancel={() => setEdit({ show: false, record: null, form: { name: '', email: '', country: '' } })}
        onOk={saveEdit}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>主播名称</Text>
            <Input
              placeholder="请输入主播名称"
              value={edit.form.name}
              onChange={(e) => setEdit({ ...edit, form: { ...edit.form, name: e.target.value } })}
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>邮箱</Text>
            <Input
              placeholder="请输入邮箱地址"
              value={edit.form.email}
              onChange={(e) => setEdit({ ...edit, form: { ...edit.form, email: e.target.value } })}
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>国家</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="请选择国家"
              value={edit.form.country || undefined}
              onChange={(value) => setEdit({ ...edit, form: { ...edit.form, country: value || '' } })}
              showSearch
              allowClear
              filterOption={(input, option) => {
                const label = option?.label || '';
                const value = option?.value || '';
                return label.toLowerCase().includes(input.toLowerCase()) ||
                  value.toLowerCase().includes(input.toLowerCase());
              }}
              options={countryOptions}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
}
